import { Table } from '@ntconsult/aluma-react';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SummaryResponse, SummaryRow, SummaryUnit } from '../types/Summary';
import { formatDisplayDate } from '../utils/data';
import { contractLabel } from '../utils/labels';
import {
  COLUMN_MIN_WIDTH,
  STICKY_START_CELL_PLAIN,
  STICKY_START_HEADER_PLAIN,
  WIDE_TABLE_CONTAINER,
} from './wideTable';

/** Linha de total da unidade, com destaque. */
const TOTAL_ROW = 'border-t-2 border-border-strong bg-muted font-bold';
const TOTAL_LABEL_CELL = 'sticky left-0 bg-muted';

type SummaryTableProps = {
  data: SummaryResponse;
  unit: SummaryUnit;
};

/** Soma o headcount por semana (fallback quando o subtotal da unidade não vem). */
function sumByWeek(rows: SummaryRow[], weekCount: number): number[] {
  const totals = new Array<number>(weekCount).fill(0);
  for (const row of rows) {
    for (let i = 0; i < weekCount; i++) totals[i] += row.headcountByWeek[i] ?? 0;
  }
  return totals;
}

type HeadcountCellProps = {
  count: number;
  /** Semana anterior na mesma linha; ausente na primeira semana. */
  previous: number | undefined;
  variationLabel: string;
};

/** Destaca a variação de headcount em relação à semana anterior (i=0 sem destaque). */
function HeadcountCell({ count, previous, variationLabel }: HeadcountCellProps) {
  const trend =
    previous === undefined || count === previous ? null : count > previous ? 'up' : 'down';
  return (
    <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
      <span
        className={`inline-flex items-center justify-end gap-1 ${
          trend === 'up' ? 'text-success' : trend === 'down' ? 'text-danger' : ''
        }`}
      >
        {trend === 'up' && <TrendingUp size={14} aria-label={variationLabel} />}
        {trend === 'down' && <TrendingDown size={14} aria-label={variationLabel} />}
        {count}
      </span>
    </Table.Cell>
  );
}

/** Matriz centro de custo × semanas de UMA unidade (BR ou US), com o total ao final. */
export function SummaryTable({ data, unit }: SummaryTableProps) {
  const { t, i18n } = useTranslation('alocacao');
  const variationLabel = t('consultas.sintese.variacao');
  const weekCount = data.weeks.length;

  const rows = data.rows.filter((row) => row.unitCode === unit);
  const total =
    data.subtotals.find((subtotal) => subtotal.unitCode === unit)?.headcountByWeek ??
    sumByWeek(rows, weekCount);

  return (
    <Table density="compact" stickyHeader containerClassName={WIDE_TABLE_CONTAINER}>
      <Table.Caption className="sr-only">{t('consultas.sintese.titulo')}</Table.Caption>
      <Table.Head>
        <Table.Row>
          <Table.Header className={`${COLUMN_MIN_WIDTH.medium} ${STICKY_START_HEADER_PLAIN}`}>
            {t('consultas.sintese.centroCusto')}
          </Table.Header>
          {data.weeks.map((week) => (
            <Table.Header
              key={week.index}
              align="right"
              className="whitespace-nowrap"
              title={t('consultas.sintese.semanaDe', {
                date: formatDisplayDate(week.startDate, i18n.language),
              })}
            >
              {formatDisplayDate(week.startDate, i18n.language)}
            </Table.Header>
          ))}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {rows.map((row) => (
          <Table.Row key={`row-${row.costCenterId}`}>
            <Table.Cell className={STICKY_START_CELL_PLAIN}>
              {contractLabel({ contractCode: row.contractCode, name: row.contractName })}
            </Table.Cell>
            {row.headcountByWeek.map((count, i) => (
              <HeadcountCell
                key={data.weeks[i].index}
                count={count}
                previous={row.headcountByWeek[i - 1]}
                variationLabel={variationLabel}
              />
            ))}
          </Table.Row>
        ))}
      </Table.Body>
      <Table.Footer>
        <Table.Row className={TOTAL_ROW}>
          <Table.Cell className={TOTAL_LABEL_CELL}>{t('consultas.sintese.total')}</Table.Cell>
          {total.map((count, i) => (
            <HeadcountCell
              key={data.weeks[i].index}
              count={count}
              previous={total[i - 1]}
              variationLabel={variationLabel}
            />
          ))}
        </Table.Row>
      </Table.Footer>
    </Table>
  );
}
